/**
 * BGV Status Widget
 * 
 * Shows real-time BGV integration status and social media metrics
 */

import React from 'react'
import { Link } from 'react-router-dom'
import { 
  CheckCircle2, 
  AlertCircle, 
  XCircle, 
  RefreshCw, 
  ExternalLink,
  Calendar,
  Send,
  TrendingUp
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useBGVConnectionHealth, usePosts, useScheduledPosts } from '@/features/social/hooks/useSocialMedia'
import { formatDistanceToNow } from 'date-fns'

interface BGVStatusWidgetProps {
  compact?: boolean
}

export default function BGVStatusWidget({ compact = false }: BGVStatusWidgetProps) {
  const { data: connectionHealth, isLoading, refetch } = useBGVConnectionHealth()
  const { data: posts = [] } = usePosts()
  const { data: scheduledPosts = [] } = useScheduledPosts()

  const getStatusIcon = () => {
    if (isLoading) return <RefreshCw className="h-4 w-4 animate-spin" />
    if (!connectionHealth) return <XCircle className="h-4 w-4 text-destructive" />
    
    switch (connectionHealth.status) {
      case 'healthy':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />
      case 'degraded':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />
      case 'unhealthy':
        return <XCircle className="h-4 w-4 text-destructive" />
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />
    }
  }

  const getStatusColor = () => {
    if (!connectionHealth) return 'text-muted-foreground'
    
    switch (connectionHealth.status) {
      case 'healthy': return 'text-green-600'
      case 'degraded': return 'text-yellow-600'
      case 'unhealthy': return 'text-destructive'
      default: return 'text-muted-foreground'
    }
  }

  const getStatusMessage = () => {
    if (isLoading) return 'Checking connection...'
    if (!connectionHealth) return 'Unable to connect to BGV'
    return connectionHealth.message
  }

  // Calculate metrics
  const recentPosts = posts.filter(post => {
    const createdAt = new Date(post.created_at)
    const weekAgo = new Date()
    weekAgo.setDate(weekAgo.getDate() - 7)
    return createdAt >= weekAgo
  })

  const publishedThisWeek = recentPosts.filter(post => post.status === 'PUBLISHED').length
  const scheduledCount = scheduledPosts.length

  if (compact) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-lg">
        {getStatusIcon()}
        <span className={`text-sm font-medium ${getStatusColor()}`}>
          BGV: {connectionHealth?.status || 'Unknown'}
        </span>
        <Button variant="ghost" size="sm" onClick={() => refetch()} className="h-6 w-6 p-0">
          <RefreshCw className="h-3 w-3" />
        </Button>
      </div>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              {getStatusIcon()}
              BGV Integration
            </CardTitle>
            <CardDescription>
              Social media automation system status
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge 
              variant={connectionHealth?.status === 'healthy' ? 'default' : 'secondary'}
              className={getStatusColor()}
            >
              {connectionHealth?.status || 'Unknown'}
            </Badge>
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground">
          {getStatusMessage()}
        </div>

        {connectionHealth && (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${
                connectionHealth.database ? 'bg-green-500' : 'bg-red-500'
              }`} />
              <span>Database: {connectionHealth.database ? 'Connected' : 'Disconnected'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${
                connectionHealth.functions ? 'bg-green-500' : 'bg-red-500'
              }`} />
              <span>Functions: {connectionHealth.functions ? 'Active' : 'Inactive'}</span>
            </div>
          </div>
        )}

        {/* Quick Stats */}
        {connectionHealth?.status === 'healthy' && (
          <div className="border-t pt-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-lg font-semibold text-green-600">{publishedThisWeek}</div>
                <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <Send className="h-3 w-3" />
                  Published this week
                </div>
              </div>
              <div>
                <div className="text-lg font-semibold text-blue-600">{scheduledCount}</div>
                <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Scheduled
                </div>
              </div>
              <div>
                <div className="text-lg font-semibold text-purple-600">{posts.length}</div>
                <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <TrendingUp className="h-3 w-3" />
                  Total posts
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="flex items-center justify-between pt-2 border-t">
          <Link to="/marketing/social">
            <Button variant="outline" size="sm">
              <ExternalLink className="h-4 w-4 mr-2" />
              Manage Posts
            </Button>
          </Link>
          
          {scheduledCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {scheduledCount} ready to publish
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  )
}